import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { API_BASE } from '../config';
import './ApiDocs.css';

const ENDPOINTS = [
  {
    category: "GenAI Privacy Gateway",
    method: "POST",
    path: "/gateway/llm/sanitize",
    title: "Sanitize LLM Prompt",
    desc: "Scans prompt text for PII entities (email, card, phone, ssn, ip) and swaps them with session tokens or FPE ciphers.",
    body: {
      prompt: "Patient Alice (email: alice@test.com, card: 4532-1234-5678-9014) needs follow-up.",
      mode: "TOKENIZE"
    }
  },
  {
    category: "GenAI Privacy Gateway",
    method: "POST",
    path: "/gateway/llm/restore",
    title: "Restore LLM Completion",
    desc: "Re-identifies session tokens in LLM responses and restores original plaintext values seamlessly.",
    body: {
      sessionId: "3f8b030b-934c-4e89-8cb1-80e98ff99b0c",
      completion: "Appointment scheduled for [EMAIL_1] and billed to [CREDIT_CARD_1]."
    }
  },
  {
    category: "Tokenization & FPE",
    method: "POST",
    path: "/tokenize",
    title: "Tokenize Data (Standard or FPE)",
    desc: "Tokenizes single PII value. Set formatPreserving=true for NIST SP 800-38G FF3-1 mode (preserves card length, Luhn check, SSN format, email format).",
    body: {
      piiType: "card",
      piiValue: "4532-1234-5678-9014",
      formatPreserving: true,
      preserveLuhn: true
    }
  },
  {
    category: "Tokenization & FPE",
    method: "POST",
    path: "/tokenize/json",
    title: "Tokenize Complex JSON Payload",
    desc: "Recursively tokenizes nested JSON structures, returning a path-to-token map and rootToken.",
    body: {
      data: {
        customer: "Bob Smith",
        contact: { email: "bob@corp.com", phone: "+1 555-0199" }
      }
    }
  },
  {
    category: "Detokenization & Policy",
    method: "POST",
    path: "/detokenize",
    title: "Detokenize Token (ABAC Governed)",
    desc: "Retrieves original plaintext. Evaluates ABAC policies and applies contextual masking (REDACT, LAST_4, MASK_EMAIL, MASK_PHONE) based on caller role/context.",
    body: {
      token: "4532-8419-3829-0194"
    }
  },
  {
    category: "Policy Governance",
    method: "POST",
    path: "/admin/policies/simulate",
    title: "Simulate ABAC/RBAC Policy PDP",
    desc: "Simulates policy decision point logic against given attributes (role, action, IP, resource) without mutating production state.",
    body: {
      role: "SUPPORT",
      action: "DETOKENIZE",
      resourceType: "DATA",
      resourceName: "card",
      attributes: { ip: "192.168.1.100" }
    }
  },
  {
    category: "Compliance & Audit",
    method: "GET",
    path: "/admin/audit-log/export?format=csv",
    title: "Export Tamper-Evident Audit Logs",
    desc: "Streams cryptographically hashed audit trails in CSV or JSON format for compliance audits.",
    body: null
  }
];

const ApiDocs = () => {
  const { token, tenant } = useAuth();
  const [apiKey, setApiKey] = useState('');
  const [selectedEndpoint, setSelectedEndpoint] = useState(ENDPOINTS[0]);
  const [viewSwaggerIframe, setViewSwaggerIframe] = useState(false);
  const [copiedKey, setCopiedKey] = useState(false);
  const [copiedSnippet, setCopiedSnippet] = useState(false);

  useEffect(() => {
    const savedApiKey = localStorage.getItem('vaultize_api_key');
    if (savedApiKey) {
      setApiKey(savedApiKey);
    } else if (token) {
      fetch(`${API_BASE}/dashboard/api-keys`, {
        headers: { Authorization: `Bearer ${token}` }
      })
      .then(res => res.ok ? res.json() : [])
      .then(keys => {
        if (Array.isArray(keys) && keys.length > 0) {
          const activeKey = keys.find(k => k.enabled && !k.revoked) || keys[0];
          const val = activeKey.apiKeyPreview || activeKey.apiKey || activeKey.maskedKey;
          if (val) setApiKey(val);
        }
      })
      .catch(() => {});
    }
  }, [token]);

  const generateCurl = (endpoint) => {
    const keyHeader = apiKey ? `-H "X-API-KEY: ${apiKey}"` : `-H "X-API-KEY: <YOUR_API_KEY>"`;
    const tenantHeader = tenant?.uid ? `-H "X-TENANT-ID: ${tenant.uid}"` : `-H "X-TENANT-ID: <YOUR_TENANT_UID>"`;

    let cmd = `curl -X ${endpoint.method} "${API_BASE}${endpoint.path}" \\\n  ${keyHeader} \\\n  ${tenantHeader}`;
    if (endpoint.body) {
      cmd += ` \\\n  -H "Content-Type: application/json" \\\n  -d '${JSON.stringify(endpoint.body, null, 2)}'`;
    }
    return cmd;
  };

  const copyText = (text, setCopied) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="api-docs-page">
      <header className="docs-header">
        <div className="docs-header-left">
          <span className="docs-version-badge">OpenAPI 3.0 (OAS) • v1.0.0-GA</span>
          <h1>Developer Portal & API Reference</h1>
          <p>
            Interactive REST documentation for Vaultize multi-tenant tokenization, format-preserving encryption (FPE),
            ABAC policy evaluation, and GenAI LLM privacy gateway.
          </p>
        </div>

        <div className="docs-header-right">
          <button
            className={`view-mode-btn ${!viewSwaggerIframe ? 'active' : ''}`}
            onClick={() => setViewSwaggerIframe(false)}
          >
            📖 Interactive Reference
          </button>
          <button
            className={`view-mode-btn ${viewSwaggerIframe ? 'active' : ''}`}
            onClick={() => setViewSwaggerIframe(true)}
          >
            ⚡ Live Swagger UI
          </button>
        </div>
      </header>

      {/* Credentials Banner */}
      <div className="credentials-banner">
        <div className="cred-item">
          <span className="cred-label">TENANT UID:</span>
          <code className="cred-code">{tenant?.uid || 'tenant-default'}</code>
        </div>
        <div className="cred-item">
          <span className="cred-label">ACTIVE API KEY:</span>
          <code className="cred-code">{apiKey ? apiKey.substring(0, 18) + '...' : 'Generate key in API Keys tab'}</code>
        </div>
        <div className="cred-item">
          <span className="cred-label">SERVER HOST:</span>
          <code className="cred-code">{API_BASE}</code>
        </div>
        <button
          className="copy-cred-btn"
          onClick={() => copyText(apiKey || tenant?.uid, setCopiedKey)}
        >
          {copiedKey ? '✓ Copied' : '📋 Copy API Key'}
        </button>
      </div>

      {viewSwaggerIframe ? (
        <div className="swagger-iframe-container">
          <iframe
            src={`${API_BASE}/swagger-ui/index.html`}
            title="Vaultize OpenAPI Swagger UI"
            className="swagger-iframe"
          />
        </div>
      ) : (
        <div className="docs-body-grid">
          {/* Endpoint Sidebar */}
          <div className="endpoint-nav">
            <h3 className="nav-title">API Operations</h3>
            {ENDPOINTS.map((ep, idx) => (
              <button
                key={idx}
                className={`endpoint-nav-btn ${selectedEndpoint.path === ep.path && selectedEndpoint.method === ep.method ? 'active' : ''}`}
                onClick={() => setSelectedEndpoint(ep)}
              >
                <span className={`method-tag method-${ep.method.toLowerCase()}`}>{ep.method}</span>
                <span className="endpoint-name">{ep.title}</span>
              </button>
            ))}
          </div>

          {/* Endpoint Detail View */}
          <div className="endpoint-detail">
            <div className="endpoint-detail-header">
              <span className={`method-pill method-${selectedEndpoint.method.toLowerCase()}`}>
                {selectedEndpoint.method}
              </span>
              <code className="endpoint-path">{selectedEndpoint.path}</code>
            </div>

            <h2>{selectedEndpoint.title}</h2>
            <p className="endpoint-desc">{selectedEndpoint.desc}</p>

            <div className="spec-section">
              <h4>Authentication Headers</h4>
              <div className="headers-table">
                <div className="header-row">
                  <code>X-API-KEY</code>
                  <span>Required. Programmatic tenant API key from Vaultize console.</span>
                </div>
                <div className="header-row">
                  <code>X-TENANT-ID</code>
                  <span>Required. Tenant unique identifier (UID).</span>
                </div>
                <div className="header-row">
                  <code>Authorization</code>
                  <span>Alternative. <code>Bearer &lt;firebase_jwt&gt;</code> for interactive web applications.</span>
                </div>
              </div>
            </div>

            {selectedEndpoint.body && (
              <div className="spec-section">
                <h4>Request Body (JSON)</h4>
                <pre className="code-block json-block">
                  {JSON.stringify(selectedEndpoint.body, null, 2)}
                </pre>
              </div>
            )}

            <div className="spec-section">
              <div className="code-header">
                <h4>cURL Command Snippet</h4>
                <button
                  className="copy-snippet-btn"
                  onClick={() => copyText(generateCurl(selectedEndpoint), setCopiedSnippet)}
                >
                  {copiedSnippet ? '✓ Copied' : '📋 Copy cURL'}
                </button>
              </div>
              <pre className="code-block curl-block">
                {generateCurl(selectedEndpoint)}
              </pre>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiDocs;
